<script setup lang="ts">
import type { Font } from "mws-platform-ts-sdk";

import type { NonNullableObject } from "~/types/Utils";

const i18n = useI18n();
watch(
  () => i18n.locale.value,
  (locale) => {
    useHead({
      htmlAttrs: {
        lang: locale,
      },
    });
  },
  { immediate: true },
);

const platformStore = usePlatformStore();
const isMatchWornShirt = computed(() => platformStore.isMatchWornShirt);
const platformConfiguration = computed(
  () => platformStore.getPlatformConfiguration,
);

const platformFonts = computed(
  () =>
    platformStore.getPlatformConfiguration.styling.fonts?.filter(
      (font) => font.url && font.weight,
    ) as NonNullableObject<Font>[],
);

const platformFontStyles = computed(
  () => platformStore.getPlatformFontDefinitions,
);

const route = useRoute();
const baseUrl = computed(
  () => `https://${platformConfiguration.value.primaryDomain}`,
);
const canonicalUrl = computed(() => `${baseUrl.value}${route.path}`);

const { locale: currentLocale, defaultLocale, locales } = useI18n();
const selectedOgLocale = computed(() => {
  const foundLocale = locales.value.find(
    (locale) => locale.code === currentLocale.value,
  );

  const selectedLocale =
    foundLocale ||
    locales.value.find((locale) => locale.code === defaultLocale);

  return (selectedLocale?.ogLocale ?? "en_GB") as string;
});

const localePath = useLocalePath();

const featureToggles = useFeatureToggles();

useHead({
  templateParams: {
    schemaOrg: {
      host: baseUrl.value,
    },
  },
});

useRedditPixel();

useSchemaOrg([
  defineWebSite({
    name: platformStore.getPlatformName,
    potentialAction: featureToggles.value.platformSearch
      ? [
          defineSearchAction({
            target: "/search/{search_term_string}",
          }),
        ]
      : undefined,
  }),
]);
</script>

<template>
  <Head>
    <LayoutMetaTags />

    <Meta
      property="og:locale"
      :content="selectedOgLocale"
    />

    <Link
      rel="canonical"
      :href="canonicalUrl"
    />

    <Meta
      property="og:url"
      :content="canonicalUrl"
    />

    <Link
      v-for="locale in locales"
      :key="locale.code"
      rel="alternate"
      :href="`${baseUrl}${localePath(route.path, locale.code)}`"
      :hreflang="locale.language"
    />

    <Link
      rel="alternate"
      :href="`${baseUrl}${localePath(route.path, 'en')}`"
      hreflang="x-default"
    />

    <Link
      v-for="faviconLink in platformStore.getFavIconLinks"
      :key="faviconLink.href"
      v-bind="faviconLink"
    />

    <template v-if="!isMatchWornShirt && platformFontStyles">
      <Link
        v-for="(font, index) in platformFonts"
        :key="font.url || index"
        rel="preload"
        :href="font.url"
        as="font"
        crossorigin="anonymous"
      />

      <Style type="text/css">
        {{ platformFontStyles }}
      </Style>
    </template>
  </Head>
</template>
